import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpContext, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SKIP_LOADING_SPINNER } from '../interceptors/loading.interceptor';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseApiPath = `${environment.apiEndPoint}api/`;

  constructor(
    private httpClient: HttpClient,
    private logger: LoggerService
  ) { }

  private setHttpHeaders(accept: string = 'application/json'): HttpHeaders {
    // The authInterceptor will handle adding the Authorization header automatically
    // No need to manually get and set tokens here
    return new HttpHeaders({
      Accept: accept
    });
  }

  private formatErrors(error: any): Observable<never> {
    this.logger.error(error);

    // Preserve structured API errors so components can show backend messages.
    // so components can access error.error.message and other properties
    if (error.status === 400 || error.status === 409 || error.status === 404) {
      return throwError(() => error);
    }

    let errorMessage = error.statusText || 'An unexpected error occurred.';

    switch (error.status) {
      case 400:
        errorMessage = 'Bad request, please contact admin.';
        break;
      case 401:
        errorMessage = 'Authorization denied, please contact admin.';
        break;
      case 403:
        errorMessage = 'Access denied, please contact admin.';
        break;
      case 500:
        errorMessage = error.statusText || 'Internal server error.';
        break;
      case 503:
        errorMessage = 'Service unavailable, please try again later.';
        break;
      case 0:
        errorMessage = 'Service is down, please contact admin.';
        break;
    }

    return throwError(() => errorMessage);
  }

  get(path: string): Observable<any> {
    const headers = this.setHttpHeaders();
    return this.httpClient.get(`${this.baseApiPath}${path}`, { headers }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  // Get without showing loading spinner (for background loads)
  getWithoutSpinner(path: string): Observable<any> {
    const context = new HttpContext().set(SKIP_LOADING_SPINNER, true);
    const headers = this.setHttpHeaders();
    return this.httpClient.get(`${this.baseApiPath}${path}`, { headers, context }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  getBlob(path: string): Observable<Blob> {
    const headers = this.setHttpHeaders('*/*');
    return this.httpClient.get(`${this.baseApiPath}${path}`, {
      headers,
      responseType: 'blob'
    }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  getBlobResponse(path: string): Observable<HttpResponse<Blob>> {
    const headers = this.setHttpHeaders('*/*');
    return this.httpClient.get(`${this.baseApiPath}${path}`, {
      headers,
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  // Get blob without showing loading spinner (for background loads)
  getBlobWithoutSpinner(path: string): Observable<Blob> {
    const context = new HttpContext().set(SKIP_LOADING_SPINNER, true);
    const headers = this.setHttpHeaders('*/*');
    return this.httpClient.get(`${this.baseApiPath}${path}`, {
      headers,
      context,
      responseType: 'blob'
    }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  post(path: string, body: any = {}): Observable<any> {
    const headers = this.setHttpHeaders();
    return this.httpClient.post(`${this.baseApiPath}${path}`, body, { headers }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  // Post without showing loading spinner (for background uploads)
  postWithoutSpinner(path: string, body: any = {}): Observable<any> {
    const context = new HttpContext().set(SKIP_LOADING_SPINNER, true);
    const headers = this.setHttpHeaders();
    return this.httpClient.post(`${this.baseApiPath}${path}`, body, { headers, context }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  put(path: string, body: any = {}): Observable<any> {
    const headers = this.setHttpHeaders();
    return this.httpClient.put(`${this.baseApiPath}${path}`, body, { headers }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  delete(path: string): Observable<any> {
    const headers = this.setHttpHeaders();
    return this.httpClient.delete(`${this.baseApiPath}${path}`, { headers }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  getFile(path: string): Observable<any> {
    const fileType = path?.split('.').pop()?.toLowerCase() || '';
    const acceptType = this.getAcceptType(fileType) || 'application/octet-stream';
    const headers = this.setHttpHeaders(acceptType);
    return this.httpClient.get(`${this.baseApiPath}${path}`, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
      headers
    }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  postFile(path: string, body: any = {}): Observable<any> {
    const filename = body.fileName;
    const fileType = filename?.split('.').pop()?.toLowerCase() || '';
    const acceptType = this.getAcceptType(fileType) || 'application/octet-stream';
    const headers = this.setHttpHeaders(acceptType);
    return this.httpClient.post(`${this.baseApiPath}${path}`, body, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
      headers
    }).pipe(
      catchError((error) => this.formatErrors(error))
    );
  }

  getAcceptType(fileType: string): string {
    const mimeTypes: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      zip: 'application/zip',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      msg: 'application/vnd.ms-outlook'
    };
    return mimeTypes[fileType.toLowerCase()] || 'application/octet-stream';
  }
}
